import { CSSProperties } from 'react';
import { HEX_18232F, HEX_3D526B, HEX_68727F } from '@tanium/colors';
import { getScrollbarSize } from '@tanium/coreui-utils';
import { defineThemeValue, isDarkMode, Text1 } from '@tanium/react-theme-definition';
import { css } from '@tanium/style-system';
import { Theme, ThemeFonts } from '@tanium/theme-data';

export interface StyledFunctionArgs {
  theme: Theme;
}

const ColorScheme = defineThemeValue<CSSProperties['colorScheme']>(['only light', 'only dark']);
const ScrollbarColor = defineThemeValue<CSSProperties['scrollbarColor']>([
  'auto',
  `${HEX_3D526B} ${HEX_18232F}`,
]);

const supportsScrollbarColor = () => {
  // `CSS.supports` has broad support itself, but handle it being unsupported just in case.
  try {
    return CSS.supports('scrollbar-color: auto');
  } catch {
    return false;
  }
};

// Following same approach as css() from @tanium/style-system here. The args for this
// function are "deliberately non-specific due to conflicts between it and the typings
// for other libraries, specifically emotion".
// The assumption is that it also will be passed directly to styled().
export const getGlobalContainerStyles = (props?: Theme | StyledFunctionArgs) => {
  const { theme } = props as unknown as StyledFunctionArgs;

  const globalStyles = [
    css({
      fontFamily: ThemeFonts.Main,
      color: Text1,
      colorScheme: ColorScheme(theme),
      scrollbarColor: ScrollbarColor(theme),
    }),
  ];

  const webkitScrollbarStyles = {
    ['::-webkit-scrollbar' as string]: {
      background: HEX_18232F,
    },
    ['::-webkit-scrollbar-thumb' as string]: {
      backgroundColor: HEX_3D526B,
      borderRadius: '9px',
      backgroundClip: 'content-box',
      border: 'solid 4px transparent',
    },
    ['::-webkit-scrollbar-thumb:vertical' as string]: {
      minHeight: '24px',
    },
    ['::-webkit-scrollbar-thumb:horizontal' as string]: {
      minWidth: '24px',
    },
    ['::-webkit-scrollbar-thumb:vertical:hover' as string]: {
      backgroundColor: HEX_68727F,
    },
    ['::-webkit-scrollbar-thumb:horizontal:hover' as string]: {
      backgroundColor: HEX_68727F,
    },

    ['::-webkit-scrollbar-corner' as string]: {
      backgroundColor: HEX_18232F,
    },
  };

  /*
   * If `scrollbar-color` is not supported, override the dark mode scrollbar
   * using `-webkit-scrollbar` instead. This isn't ideal because it cannot be
   * reverted for ancestors like `scrollbar-color` can.
   * @see https://gist.git.corp.tanium.com/matthew-tingen/13d4671cf3882b16a0634c94f4454529
   *
   * Because `scrollbar-color: auto` defers to `-webkit-scrollbar`, adding the
   * prefixed styles has to be done conditionally.
   * @see https://github.com/web-platform-tests/wpt/blob/5a5bd1e6635142b99fe27a5ea731fba9385d2b90/css/css-scrollbars/scrollbar-color-003.tentative.html
   */
  if (isDarkMode(theme) && !supportsScrollbarColor()) {
    // Setting -webkit-scrollbar will force scrollbars to show even if
    // you have say scrollbars "hidden unless scrolling" via user preference.
    // Only applying styles if scrollbars are already visible to avoid forcing
    // them to always be visible.
    const scrollbarSize = getScrollbarSize();
    if (scrollbarSize > 0) {
      globalStyles.push(
        css(webkitScrollbarStyles),
        css({
          ['*' as string]: webkitScrollbarStyles,
        }),
      );
    }
  }

  return globalStyles;
};
